import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Save, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export function ChecklistTemplateEditor({ kind = "vano" }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newSection, setNewSection] = useState("");

  useEffect(() => { api.get("/checklist/template", { params: { kind } }).then((r) => setItems(r.data)); }, [kind]);

  const sections = [...new Set(items.map((i) => i.section))];
  const update = (idx, patch) => setItems((p) => p.map((x, j) => (j === idx ? { ...x, ...patch } : x)));
  const remove = (idx) => setItems((p) => p.filter((_, j) => j !== idx));
  const addTo = (section) => setItems((p) => {
    const last = p.map((x) => x.section).lastIndexOf(section);
    const row = { id: null, section, name: "", qty: "", has_expiry: false };
    return last < 0 ? [...p, row] : [...p.slice(0, last + 1), row, ...p.slice(last + 1)];
  });
  const addSection = () => {
    const s = newSection.trim().toUpperCase();
    if (!s) return;
    addTo(s);
    setNewSection("");
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/checklist/template", { items }, { params: { kind } });
      setItems(data);
      toast.success("Voci della check-list aggiornate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="template-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Modifica descrizioni e quantità secondo le linee guida vigenti. Le modifiche si applicano anche alle check-list già compilate.</p>
        <Button onClick={save} disabled={saving} data-testid="template-save" className="rounded-full"><Save className="h-4 w-4 mr-1" /> {saving ? "Salvataggio..." : "Salva modifiche"}</Button>
      </div>
      {sections.map((s) => (
        <div key={s} className="rounded-lg border border-border">
          <p className="bg-muted px-3 py-2 text-xs font-bold uppercase tracking-wide">{s}</p>
          <div className="p-3 space-y-2">
            {items.map((it, idx) => it.section !== s ? null : (
              <div key={it.id || `new-${idx}`} className="flex gap-2 items-center" data-testid={`template-row-${idx}`}>
                <Input value={it.name} onChange={(e) => update(idx, { name: e.target.value })} placeholder="Descrizione dispositivo" data-testid={`template-name-${idx}`} className="h-8 text-sm" />
                <Input value={it.qty} onChange={(e) => update(idx, { qty: e.target.value })} placeholder="n°" data-testid={`template-qty-${idx}`} className="h-8 text-sm w-20 shrink-0" />
                <Button size="sm" type="button" variant={it.has_expiry ? "default" : "outline"} title="Voce con data di scadenza" onClick={() => update(idx, { has_expiry: !it.has_expiry })} data-testid={`template-expiry-${idx}`} className={`h-8 shrink-0 rounded-full text-xs ${it.has_expiry ? "bg-orange-600 hover:bg-orange-700" : ""}`}>
                  <CalendarClock className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Scadenza</span>
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(idx)} data-testid={`template-delete-${idx}`} className="h-8 w-8 shrink-0 text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="ghost" onClick={() => addTo(s)} data-testid={`template-add-${s}`} className="h-7 text-xs"><Plus className="h-3 w-3 mr-1" /> Aggiungi voce</Button>
          </div>
        </div>
      ))}
      <div className="flex gap-2 max-w-md">
        <Input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="Nuova sezione" data-testid="template-new-section" />
        <Button variant="outline" onClick={addSection} data-testid="template-add-section" className="rounded-full shrink-0"><Plus className="h-4 w-4 mr-1" /> Sezione</Button>
      </div>
    </div>
  );
}
